/* Preserve the native cart bar; Shopify's shipping quote is the authority. */
(() => {
  if (customElements.get('co-cart-shipping')) return;
  const quotes = new Map();
  // Representative zone-estimate postcodes, never entered into checkout forms.
  // The verified General profile has one identical rate for these five countries.
  const zones = {
    US: ['United States', 'New York', '10001'],
    CA: ['Canada', 'Ontario', 'M5V2T6'],
    AU: ['Australia', 'New South Wales', '2000'],
    GB: ['United Kingdom', '', 'SW1A1AA'],
    NZ: ['New Zealand', 'Auckland', '1010']
  };
  async function quote(key, country) {
    const cached = quotes.get(key);
    if (cached) return cached;
    const address = zones[country];
    if (!address) throw new Error('Unsupported shipping zone');
    const params = new URLSearchParams({
      'shipping_address[country]': address[0],
      'shipping_address[province]': address[1],
      'shipping_address[zip]': address[2]
    });
    const promise = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/shipping_rates.json?${params}`, {
          credentials: 'same-origin', headers: {Accept: 'application/json'}, signal: controller.signal
        });
        if (!response.ok) throw new Error('Shipping estimate unavailable');
        const data = await response.json();
        if (!Array.isArray(data.shipping_rates) || !data.shipping_rates.length) throw new Error('No rates');
        return data.shipping_rates;
      } finally { clearTimeout(timeout); }
    })();
    quotes.set(key, promise);
    // Only deduplicate in-flight estimates; never reuse a quote for an old cart.
    promise.then(() => quotes.delete(key), () => quotes.delete(key));
    return promise;
  }
  class CartShipping extends HTMLElement {
    connectedCallback() { this.revision = (this.revision || 0) + 1; this.update(this.revision); }
    disconnectedCallback() { this.revision = (this.revision || 0) + 1; }
    async update(revision) {
      const text = this.querySelector('.cart-progress__text');
      const bar = this.querySelector('.cart-progress__bar');
      const fill = this.querySelector('.cart-progress__bar__progress');
      if (!text || !bar || !fill) return;
      const total = Number(this.dataset.cartTotal), currency = this.dataset.currency;
      if (!Number(this.dataset.itemCount)) {
        text.textContent = 'Add more to unlock FREE shipping!';
        fill.style.width = '0%'; this.dataset.shippingStatus = 'empty'; return;
      }
      // Serum offers use the merchant's $50 cart threshold, independent of the brush offer.
      const offerGoal = Number(this.dataset.offerShippingGoalUsd);
      if (offerGoal > 0 && currency === 'USD') {
        const free = total >= offerGoal;
        this.hidden = false; this.style.removeProperty('display'); bar.hidden = false;
        this.dataset.shippingStatus = free ? 'free' : 'paid';
        this.dataset.shippingSource = 'merchant-offer';
        text.replaceChildren(this.querySelector(free ? '[data-shipping-success]' : '[data-shipping-progress]').content.cloneNode(true));
        const amount = text.querySelector('[data-shipping-remaining]');
        if (amount) amount.textContent = new Intl.NumberFormat('en-US', {
          style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2
        }).format(Math.max(0, offerGoal - total) / 100);
        fill.style.width = `${Math.min(100, total / offerGoal * 100)}%`;
        return;
      }
      const key = [this.dataset.country, currency, total, this.dataset.cartLines].join('|');
      try {
        const rates = await quote(key, this.dataset.country);
        if (!this.isConnected || revision !== this.revision) return;
        const valid = rates.filter(r => r.currency === currency && Number.isFinite(Number(r.price)));
        if (!valid.length) throw new Error('Shipping currency mismatch');
        const free = valid.some(r => Number(r.price) === 0);
        this.dataset.shippingStatus = free ? 'free' : 'paid';
        this.dataset.shippingSource = 'shopify';
        if (free) {
          text.replaceChildren(this.querySelector('[data-shipping-success]').content.cloneNode(true));
          fill.style.width = '100%';
        } else {
          // These variants have the merchant's dedicated paid shipping profile.
          // Spending more does not unlock free shipping for this profile.
          const fixedProfileVariants = ['55166348427607','55166348460375','55166348493143','55327288951127','55327289016663','55327110201687'];
          const lines = this.dataset.cartLines || '';
          if (fixedProfileVariants.some(id => lines.includes(id))) {
            this.hidden = true;
            this.style.display = 'none';
            return;
          }
          const exchange = Number(window.Shopify?.currency?.rate);
          const goal = currency === 'USD' ? 5000 : 5000 * exchange;
          // International shipping eligibility can differ from product FX/rounding.
          // Never invent an exact remaining local amount from an approximate rate.
          if (currency === 'USD' && total < 5000) {
            text.replaceChildren(this.querySelector('[data-shipping-progress]').content.cloneNode(true));
            const amount = text.querySelector('[data-shipping-remaining]');
            if (amount) amount.textContent = new Intl.NumberFormat('en-US', {
              style:'currency', currency:'USD', minimumFractionDigits:0, maximumFractionDigits:2
            }).format((5000 - total) / 100);
          } else text.textContent = 'Add more to unlock FREE shipping!';
          fill.style.width = `${Number.isFinite(goal) && goal > 0 ? Math.min(95, total / goal * 100) : 0}%`;
        }
      } catch (_) {
        if (!this.isConnected || revision !== this.revision) return;
        this.dataset.shippingStatus = 'unavailable';
        text.textContent = 'Shipping calculated at checkout';
        bar.hidden = true;
      }
    }
  }
  customElements.define('co-cart-shipping', CartShipping);
})();
